const QUOTA_RE = /429|too many requests|connection minutes|minutes limit|quota exceeded|livekit_quota_exceeded/i
/** Signal/token auth only — do NOT match media "permission denied" */
const AUTH_RE = /invalid token|unauthorized|not allowed to join|could not establish signal connection|livekit rejected/i
/** React remount / Strict Mode / connect abort — not a real user-facing failure */
const BENIGN_DISCONNECT_RE =
	/client initiated disconnect|abort connection attempt|connection cancelled|user initiated disconnect/i
const DEVICE_NOT_FOUND_RE =
	/requested device not found|notfounderror|could not start video source|could not start audio source|device id|no camera|no microphone/i
const DEVICE_PERMISSION_RE =
	/notallowederror|permission denied|permission dismissed|user denied permission|permission denied by system/i
const DEVICE_BUSY_RE =
	/notreadableerror|could not start audio source|could not start video source|device in use|track start error/i

export function isLiveKitQuotaError(err) {
  const msg = err?.message || String(err || '')
  return QUOTA_RE.test(msg) || err?.code === 'LIVEKIT_QUOTA_EXCEEDED'
}

export function isLiveKitAuthError(err) {
  const msg = err?.message || String(err || '')
  // Media permission is not an auth failure
  if (isLiveKitDevicePermissionError(err) || isLiveKitDeviceNotFoundError(err)) return false
  return AUTH_RE.test(msg)
}

/** True when LiveKit aborted connect because React remounted or connect=false mid-join. */
export function isLiveKitBenignDisconnect(err) {
  const msg = err?.message || String(err || '')
  const reason = err?.reason != null ? String(err.reason) : ''
  return BENIGN_DISCONNECT_RE.test(msg) || BENIGN_DISCONNECT_RE.test(reason)
}

export function isLiveKitDeviceNotFoundError(err) {
  const msg = err?.message || String(err || '')
  const name = err?.name != null ? String(err.name) : ''
  return name === 'NotFoundError' || DEVICE_NOT_FOUND_RE.test(msg)
}

export function isLiveKitDevicePermissionError(err) {
  const msg = err?.message || String(err || '')
  const name = err?.name != null ? String(err.name) : ''
  return name === 'NotAllowedError' || DEVICE_PERMISSION_RE.test(msg)
}

export function isLiveKitDeviceBusyError(err) {
  const msg = err?.message || String(err || '')
  const name = err?.name != null ? String(err.name) : ''
  return name === 'NotReadableError' || DEVICE_BUSY_RE.test(msg)
}

/** Camera/mic issues that must NOT block school join */
export function isLiveKitRecoverableMediaError(err) {
  return (
    isLiveKitDeviceNotFoundError(err)
    || isLiveKitDevicePermissionError(err)
    || isLiveKitDeviceBusyError(err)
  )
}

export function getLiveKitMediaWarning(err) {
  if (isLiveKitDevicePermissionError(err)) {
    return 'Camera/microphone is not allowed. Please Allow camera & mic in the browser address bar. You are still in the meeting without camera.'
  }
  if (isLiveKitDeviceNotFoundError(err)) {
    return 'No camera/microphone found on this computer. You are still in the meeting without camera. Plug in a webcam when available.'
  }
  if (isLiveKitDeviceBusyError(err)) {
    return 'Camera/microphone is busy in another app. Close Zoom/Teams/OBS and enable camera from controls. You are still in the meeting.'
  }
  return 'Camera could not start. You are still in the meeting without camera — please allow camera permission if prompted.'
}

export function getLiveKitUserMessage(err) {
  const msg = err?.message || String(err || '')
  if (isLiveKitBenignDisconnect(err)) {
    return ''
  }
  if (isLiveKitQuotaError(err)) {
    return 'Live video session limit reached on your LiveKit Cloud plan (monthly connection minutes). Please ask your administrator to upgrade the plan or wait until the quota resets, then try again.'
  }
  // Media issues are warnings + soft join — never a hard connection error message for school kiosk
  if (isLiveKitRecoverableMediaError(err)) {
    return ''
  }
  if (isLiveKitAuthError(err)) {
    return 'LiveKit rejected the video token. Ask your administrator to verify LIVEKIT_API_KEY and LIVEKIT_API_SECRET in backend .env match the LiveKit Cloud project, then restart the backend.'
  }
  return msg || 'Could not connect to the video session. Please try again.'
}

export function getFetchErrorMessage(err, fallback = 'Could not reach the server.') {
  const msg = err?.message || String(err || '')
  if (msg === 'Failed to fetch' || /networkerror|load failed/i.test(msg)) {
    return 'Could not reach the backend server. Make sure the backend is running and REACT_APP_BASE_URL is correct (default http://localhost:3004).'
  }
  return msg || fallback
}
